import type { GitFile } from '../transport/api'

/**
 * WHAT THE STATUS MEANS — Source Control's decisions, out of its component.
 *
 * Extracted from GitPanel.tsx in M5 (把純邏輯搬出元件, 2026-09-03). Nothing here was
 * rewritten: `groups`, the letter tables and the path helpers moved verbatim, because
 * this extraction's entire value is that the DECISIONS become directly testable —
 * before this, the census measured that reaching `groups()` took mounting the whole
 * panel and mocking fifteen transport functions, so the repo's densest classification
 * logic had exactly one test angle and it was the widest one.
 *
 * The rules encoded here, each with a way to be wrong that a mounted-panel test never
 * isolated:
 *
 *   · A path whose staged×unstaged codes form one of git's UNMERGED pairs is a
 *     CONFLICT, listed once under Merge Changes and removed from both other groups —
 *     double-listing a conflicted file offers stage/unstage on something git will
 *     refuse to half-touch.
 *   · Changes = unstaged + untracked, one list, sorted together — VS Code's shape.
 *   · git reports an entirely-untracked DIRECTORY collapsed with a trailing slash
 *     ("src/git/"); naive last-slash splitting renders such a row blank.
 */

/** git's unmerged pairs (git-status(1) "Short Format"). Both sides matter. */
export const UNMERGED = new Set(['DD', 'AU', 'UD', 'UA', 'DU', 'AA', 'UU'])

export const LETTER: Record<string, string> = {
  M: 'M', A: 'A', D: 'D', R: 'R', C: 'C', T: 'T', U: 'U', '?': 'U',
}
export const TITLE: Record<string, string> = {
  M: 'Modified', A: 'Added', D: 'Deleted', R: 'Renamed', C: 'Copied',
  T: 'Type changed', U: 'Conflict', '?': 'Untracked',
}

export type Row = { path: string; code: string; kind: 'staged' | 'unstaged' | 'untracked' }

/** Split a status payload into VS Code's three groups. */
export function groups(s: {
  staged: GitFile[]
  unstaged: GitFile[]
  untracked: string[]
}): { conflicts: Row[]; staged: Row[]; changes: Row[] } {
  const stagedBy = new Map(s.staged.map((f) => [f.path, f.code]))
  const unstagedBy = new Map(s.unstaged.map((f) => [f.path, f.code]))

  const conflicted = new Set<string>()
  for (const [path, x] of stagedBy) {
    const y = unstagedBy.get(path)
    if (y && UNMERGED.has(`${x}${y}`)) conflicted.add(path)
  }

  return {
    conflicts: [...conflicted].sort().map((path) => ({ path, code: 'U', kind: 'unstaged' as const })),
    staged: s.staged
      .filter((f) => !conflicted.has(f.path))
      .map((f) => ({ path: f.path, code: f.code, kind: 'staged' as const })),
    changes: [
      ...s.unstaged
        .filter((f) => !conflicted.has(f.path))
        .map((f) => ({ path: f.path, code: f.code, kind: 'unstaged' as const })),
      ...s.untracked.map((path) => ({ path, code: '?', kind: 'untracked' as const })),
    ].sort((a, b) => a.path.localeCompare(b.path)),
  }
}

/**
 * git reports an entirely-untracked DIRECTORY collapsed, with a trailing slash
 * ("src/git/"), not as its individual files. Naively splitting on the last "/" gives
 * an empty name and the whole path as the parent, so such a row rendered blank.
 */
export const isDir = (p: string) => p.endsWith('/')
const trimmed = (p: string) => (isDir(p) ? p.slice(0, -1) : p)
export const baseName = (p: string) => {
  const t = trimmed(p)
  return t.slice(t.lastIndexOf('/') + 1) + (isDir(p) ? '/' : '')
}
export const dirName = (p: string) => {
  const t = trimmed(p)
  return t.includes('/') ? t.slice(0, t.lastIndexOf('/')) : ''
}
